import { User } from "@supabase/supabase-js";
import { Profile } from "@core/contexts/AuthContext";
import { supabase } from "@core/lib/supabase";

/**
 * Vérifie si l'utilisateur actuel est un administrateur — version synchrone.
 *
 * Ordre des checks, du plus authoritative au moins :
 * 1. JWT app_metadata.roles.includes('admin')
 * 2. profile.preferences.role === 'admin'
 *
 * Ne peut PAS lire la table admin_users depuis cette fonction (sync). Pour ça
 * on expose `checkIsAdminAsync` qui fait un SELECT sur admin_users. Le guard
 * `AdminGuard` fait d'abord ce check sync, puis si négatif, fallback async.
 *
 * La whitelist d'emails hardcodée a été retirée : elle est remplacée par
 * la table `admin_users` (cf. migration 20260415_admin_users_table.sql).
 */
export const checkIsAdmin = (
  user: User | null,
  profile: Profile | null,
): boolean => {
  if (!user) return false;

  const userRoles = user.app_metadata?.roles || [];
  if (Array.isArray(userRoles) && userRoles.includes("admin")) {
    return true;
  }

  const profileRole = profile?.preferences?.role;
  if (profileRole === "admin") {
    return true;
  }

  return false;
};

/**
 * Vérifie de façon asynchrone si l'user courant est dans la table
 * `admin_users`. Utilise la fonction SQL helper `public.is_admin()`
 * (SECURITY DEFINER) pour éviter tout problème de RLS récursif.
 *
 * Renvoie false si pas authentifié ou si la RPC échoue.
 */
export const checkIsAdminAsync = async (
  user: User | null,
): Promise<boolean> => {
  if (!user) return false;
  try {
    const { data, error } = await supabase.rpc("is_admin");
    if (error) {
      console.warn("[adminCheck] is_admin RPC failed:", error.message);
      return false;
    }
    return Boolean(data);
  } catch (err) {
    console.warn("[adminCheck] is_admin RPC threw:", err);
    return false;
  }
};
